import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TasksView from '../components/screens/TasksView';
import useFamilyPresence from '../hooks/useFamilyPresence';
import { useTasks } from '../hooks/useTasks';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../lib/dateTime';
import {
  buildRecurrencePayload,
  createRecurrenceForm,
} from '../lib/recurrence';

const initialTaskForm = {
  title: '',
  description: '',
  assignedTo: '',
  priority: 'medium',
  dueDate: '',
  tags: '',
  scope: 'personal',
  recurrence: createRecurrenceForm(),
};

function TasksPage({ auth }) {
  const location = useLocation();
  const tasksParams = useMemo(
    () => ({
      focusTaskId: new URLSearchParams(location.search).get('focusTaskId') || '',
      view: new URLSearchParams(location.search).get('view') || '',
    }),
    [location.search]
  );
  const [taskForm, setTaskForm] = useState(initialTaskForm);
  const [editingTaskId, setEditingTaskId] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const { hasFamily } = useFamilyPresence(auth.currentUser);

  const {
    tasks,
    taskFilters,
    taskError,
    isLoadingTasks,
    refreshTasks,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
    setTaskFilters,
  } = useTasks(auth.currentUser);

  const resetTaskForm = () => {
    setEditingTaskId('');
    setTaskForm(initialTaskForm);
  };

  const toTaskForm = (task) => ({
    title: task?.title || '',
    description: task?.description || '',
    assignedTo: task?.assignedTo || '',
    priority: task?.priority || 'medium',
    dueDate: toDateTimeLocalValue(task?.dueDate),
    tags: Array.isArray(task?.tags) ? task.tags.join(', ') : '',
    scope: task?.scope || 'personal',
    recurrence: createRecurrenceForm(task?.recurrence),
  });

  const handleCreateTask = async (event) => {
    event.preventDefault();
    const payload = {
      title: taskForm.title,
      description: taskForm.description,
      assignedTo: taskForm.assignedTo,
      priority: taskForm.priority,
      dueDate: fromDateTimeLocalValue(taskForm.dueDate),
      tags: taskForm.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      scope: taskForm.scope,
      recurrence: buildRecurrencePayload(taskForm.recurrence),
    };

    const succeeded = editingTaskId
      ? await updateTask(editingTaskId, payload)
      : await createTask(payload, { canUseFamilyScope: hasFamily });

    if (succeeded) {
      resetTaskForm();
    }
  };

  const handleEditTask = (task) => {
    setEditingTaskId(task?.id || '');
    setTaskForm(toTaskForm(task));
  };

  const handleDeleteTask = async (taskId) => {
    const deleted = await deleteTask(taskId);
    if (deleted && editingTaskId === taskId) {
      resetTaskForm();
    }
  };

  // Client-side scope filter — all tasks already loaded from the unified endpoint
  const visibleTasks = scopeFilter === 'all'
    ? tasks
    : tasks.filter((t) => t.scope === scopeFilter);

  return (
    <TasksView
      tasksStatus={{
        type: isLoadingTasks ? 'loading' : taskError ? 'error' : 'success',
        message: taskError
          ? taskError
          : isLoadingTasks
          ? 'Loading tasks...'
          : tasks.length
          ? 'Tasks loaded.'
          : 'No tasks loaded yet.',
      }}
      tasksParams={tasksParams}
      taskFilters={{ ...taskFilters, scope: scopeFilter }}
      taskForm={taskForm}
      editingTaskId={editingTaskId}
      canUseFamilyScope={hasFamily}
      tasksData={visibleTasks}
      onTaskFilterChange={(field, value) => {
        if (field === 'scope') {
          setScopeFilter(value);
        } else {
          setTaskFilters((current) => ({ ...current, [field]: value }));
        }
      }}
      onRefresh={refreshTasks}
      onCreateTask={handleCreateTask}
      onTaskFormChange={(field, value) => setTaskForm((current) => ({ ...current, [field]: value }))}
      onUpdateTask={updateTask}
      onCompleteTask={completeTask}
      onEditTask={handleEditTask}
      onDeleteTask={handleDeleteTask}
      onCancelTaskEdit={resetTaskForm}
    />
  );
}

export default TasksPage;
